import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { UserPublicProfile } from '@user/PublicProfile';
import { useUserPublicProfile } from '@user/PublicProfile/useUserPublicProfile';
import { useUserPublicProfiles } from '@user/PublicProfile';
import { Loading } from '@framework/ui/atoms/Loading';
import { classNames, generateDefaultUserIcon } from '@framework/utils';
import type { WorkspaceMemberRoles } from '@workspace/domain/workspace';
import { WorkspaceMemberRole } from '@workspace/domain/workspace';
import type { GroupMemberRolesJSON, WorkspaceInternalPublicMemberRoleJSON } from 'balus-common';

type Props = {
  currentUserId: string;
  memberRoles: WorkspaceMemberRoles;
  groupMemberRoles: GroupMemberRolesJSON;
  internalPublicSetting: WorkspaceInternalPublicMemberRoleJSON | null;
  disabled: boolean;
  selectedUserIds: string[];
  onChange(userIds: string[]): void;
  memberFilter: (name: string) => boolean;
};

export const MemberRemoveList: React.FC<Props> = ({
  currentUserId,
  memberRoles,
  groupMemberRoles,
  internalPublicSetting,
  disabled,
  selectedUserIds,
  onChange,
  memberFilter,
}: Props) => {
  const userIds = useMemo(() => {
    return memberRoles.userIds().filter((id) => id !== currentUserId);
  }, [memberRoles, currentUserId]);
  const myProfile = useUserPublicProfile(currentUserId);
  const myRole = memberRoles.getRoleOf(currentUserId);
  const profiles = useUserPublicProfiles(userIds);

  const handleChange = useCallback(
    (userId: string, checked: boolean) => {
      if (checked) {
        onChange([...selectedUserIds, userId]);
      } else {
        onChange(selectedUserIds.filter((id) => id !== userId));
      }
    },
    [onChange, selectedUserIds]
  );

  if (!myProfile || !myRole || !profiles) return <Loading />;

  return (
    <ul className="px-2">
      <MemberItem
        removable={false}
        profile={myProfile}
        role={myRole}
        isGuest={false}
        disabled={disabled}
        checked={false}
        onChange={() => void 0}
      />

      {profiles.map((profile) => {
        const role = memberRoles.getRoleOf(profile.id);
        if (!role) return null;
        if (!memberFilter(profile.name)) return null;

        return (
          <MemberItem
            key={profile.id}
            removable={true}
            profile={profile}
            role={role}
            isGuest={groupMemberRoles[profile.id] === 'guest'}
            disabled={disabled || internalPublicSetting ? groupMemberRoles[profile.id] !== 'guest' : false}
            checked={selectedUserIds.includes(profile.id)}
            onChange={handleChange}
          />
        );
      })}
    </ul>
  );
};

type ItemProps = {
  profile: UserPublicProfile;
  role: WorkspaceMemberRole;
  isGuest: boolean;
  removable: boolean;
  disabled: boolean;
  checked: boolean;
  onChange(userId: string, checked: boolean): void;
};

const MemberItem: React.FC<ItemProps> = ({
  profile,
  role,
  isGuest,
  removable,
  disabled,
  checked,
  onChange,
}: ItemProps) => {
  const { t } = useTranslation();
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(profile.id, event.target.checked);
    },
    [onChange, profile.id]
  );

  return (
    <li className="flex flex-row items-center px-3 py-2 hover:bg-gray-200">
      <label className="flex grow cursor-pointer flex-row truncate">
        <input
          type="checkbox"
          className={classNames(
            'form-checkbox mr-1 h-6 w-6 border-brand',
            disabled || !removable ? 'cursor-not-allowed bg-gray-300' : 'cursor-pointer'
          )}
          checked={checked}
          onChange={handleChange}
          disabled={disabled || !removable}
        />
        <span className="flex size-6 overflow-hidden rounded-full">
          {profile.imageUrl ? (
            <img className="size-full object-cover" src={profile.imageUrl} alt={profile.name} />
          ) : (
            <div className="size-full object-cover">
              {generateDefaultUserIcon({
                id: profile.id,
                name: profile.name,
                isAnonymous: profile.isAnonymous,
                size: 24,
              })}
            </div>
          )}
        </span>
        <span className="flex h-6 grow truncate px-2 leading-6">{profile.name}</span>
        {isGuest && (
          <span className="mx-1 rounded-lg bg-gray-400 px-2 text-xs">{t('workspace.individualPermission.guest')}</span>
        )}
      </label>
      <span className="flex w-20 justify-end">{t(WorkspaceMemberRole.getLocalStringKey(role))}</span>
    </li>
  );
};
